import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import { useCheckSubSectionAccess } from 'hooks/useCheckSubSectionAccess/useCheckSubSectionAccess';

import { FieldArrayComponent } from 'components/_basic/Form/FieldArray';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FormHeader } from 'components/_basic/FormHeader/FormHeader';
import { FormSectionCard } from 'components/_basic/FormSectionCard/FormSectionCard';
import { FORM_SECTION_CARD_VARIANT } from 'components/_basic/FormSectionCard/types';

import { concatWithDot } from 'helpers/string';

import { DevelopmentTimelineRow } from './DevelopmentTimelineRow';
import { prefix, PRODUCTS_AND_SERVICES_R_AND_D_NAMES, timelineInitialValue } from './utils/form';
import { GLOBAL_FIELD_NAMES, globalPrefix } from '../../Global/form';

export const ProductAndServicesRAndD: React.FC = () => {
  const { t } = useTranslation();
  const { control } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.PRODUCTS_AND_SERVICES,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_PRODUCTS_AND_SERVICES.R_AND_D,
  );

  return (
    <>
      <FormSectionCard variant={FORM_SECTION_CARD_VARIANT.LARGE}>
        <FormHeader
          locked={!canAccess}
          title={t('productsAndServicesForm.title')}
          badgeText={t('productsAndServicesForm.researchAndDevelopment.title')}
          section={FOR_BANK_SECTIONS.PRODUCTS_AND_SERVICES}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('productsAndServicesForm.researchAndDevelopment.whtIsReadinessLevelForMarket')}
        aiFieldName={concatWithDot(prefix, PRODUCTS_AND_SERVICES_R_AND_D_NAMES.READINESS_LEVEL)}
      >
        {/* 3.3.1 */}
        <TextInputControl
          multiline
          control={control}
          id="productsAndServicesForm.rAndD.readinessLevel"
          name={concatWithDot(prefix, PRODUCTS_AND_SERVICES_R_AND_D_NAMES.READINESS_LEVEL)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('productsAndServicesForm.researchAndDevelopment.conductedRAndDActivities')}
        aiFieldName={concatWithDot(prefix, PRODUCTS_AND_SERVICES_R_AND_D_NAMES.CONDUCTED_ACTIVITIES)}
      >
        {/* 3.3.2 */}
        <TextInputControl
          multiline
          control={control}
          id="productsAndServicesForm.rAndD.conductedActivities"
          name={concatWithDot(prefix, PRODUCTS_AND_SERVICES_R_AND_D_NAMES.CONDUCTED_ACTIVITIES)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('productsAndServicesForm.researchAndDevelopment.productDevelopmentTimeline')}
        collapsible
      >
        {/* 3.3.3 */}
        <FieldArrayComponent
          name={concatWithDot(prefix, PRODUCTS_AND_SERVICES_R_AND_D_NAMES.TIMELINES)}
          initialItem={timelineInitialValue}
          buttonText={t('stage')}
          disabled={!canAccess}
        >
          {(namePrefix) => <DevelopmentTimelineRow name={namePrefix} disabled={!canAccess} />}
        </FieldArrayComponent>
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('productsAndServicesForm.researchAndDevelopment.resourcesNeededToComplete')}
        aiFieldName={concatWithDot(prefix, PRODUCTS_AND_SERVICES_R_AND_D_NAMES.RESOURCES)}
      >
        {/* 3.3.4 */}
        <TextInputControl
          multiline
          control={control}
          id="productsAndServicesForm.rAndD.resources"
          name={concatWithDot(prefix, PRODUCTS_AND_SERVICES_R_AND_D_NAMES.RESOURCES)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('productsAndServicesForm.researchAndDevelopment.weaknessToFix')}
        aiFieldName={concatWithDot(prefix, PRODUCTS_AND_SERVICES_R_AND_D_NAMES.WEAKNESSES)}
      >
        {/* 3.3.5 */}
        <TextInputControl
          multiline
          control={control}
          id="productsAndServicesForm.rAndD.weaknesses"
          name={concatWithDot(prefix, PRODUCTS_AND_SERVICES_R_AND_D_NAMES.WEAKNESSES)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
      <FormSectionCard
        disabled={!canAccess}
        title={t('productsAndServicesForm.researchAndDevelopment.planForFutureDevelopment')}
        aiFieldName={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PRODUCT_DEVELOPMENT_PLANS)}
      >
        {/* 3.3.6 */}
        <TextInputControl
          multiline
          control={control}
          id="productsAndServicesForm.rAndD.developmentPlans"
          name={concatWithDot(globalPrefix, GLOBAL_FIELD_NAMES.PRODUCT_DEVELOPMENT_PLANS)}
          minRows={4}
          disabled={!canAccess}
          maxLength={1500}
        />
      </FormSectionCard>
    </>
  );
};
